import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CommonTable from "../../components/commonComponent/CommanTable";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

export default function ItemView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  // Fetch price-client data from API by id
  const fetchItem = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(`/admin/api/priceCategory/${id}`);
      const resData = response.data || {};

      if (resData.Status === 1 || resData.status === 'success' || resData.status === 1) {
        const payload = resData.data?.priceCategory || resData.priceCategory || resData.data || resData;
        setItem(payload);
        toastSuccess(resData.Message || resData.message || 'Fetched successfully');
      } else {
        const errorMessage = resData.Message || resData.message || "Failed to fetch record";
        setError(errorMessage);
        toastError(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching price client:", error);
      const errorMessage = error.response?.data?.Message || "Failed to fetch record. Please try again.";
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchItem();
    }
  }, [id]);

  // Prepare variant data for table (show original price and offer price)
  const variantRows = (item?.variants || item?.items || []).map((variant, index) => {
    const v = variant.item || variant.variant || variant;
    const name = v?.name || variant?.name || variant?.itemName || '';
    const originalPrice = v?.salesPrice || v?.rate || v?.price || variant?.originalPrice || '';
    const offerPrice = variant?.finalPrice || variant?.offerPrice || variant?.discountedPrice || '';
    return {
      ...variant,
      srNo: index + 1,
      name,
      originalPrice: originalPrice ? `₹${originalPrice}` : 'N/A',
      offerPrice: offerPrice ? `₹${offerPrice}` : 'N/A',
    };
  }) || [];

  const filteredRows = variantRows.filter(row =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );
  const offerNameDisplay = item?.offerName || item?.name || '';
  const categoryName = item?.userCategory?.name || item?.category?.name || '';
  const startDateDisplay = item?.startDate ? String(item.startDate).split('T')[0] : '';
  const endDateDisplay = item?.endDate ? String(item.endDate).split('T')[0] : '';
  const totalItems = (item?.items || []).length;
  const lastUpdateDisplay = item?.updatedDate || item?.updatedAt || item?.updatedAt || '';
  const offerPeriodDays = (item?.startDate && item?.endDate) ? (Math.ceil((new Date(item.endDate) - new Date(item.startDate)) / (1000 * 60 * 60 * 24))) : null;

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading item details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/user/price')}
        >
          <i className="fas fa-arrow-left me-1"></i> Back to Clients
        </button>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="mb-3">
            <i className="fas fa-inbox fa-3x text-muted"></i>
          </div>
          <h5 className="text-muted">Client not found</h5>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/user/price')}
          >
            <i className="fas fa-arrow-left me-1"></i> Back to price Lists
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ fontFamily: "inherit", maxWidth: 1200 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="fw-bold" style={{ fontSize: "2rem" }}>View Price List</h1>
        <div className="d-flex gap-2 align-items-center">
          <a href="/admin/price" className="text-decoration-none text-secondary me-2">&larr; Price List</a>
          <button className="btn btn-sm btn-primary" onClick={() => navigate(`/admin/categoryOffer/edit/${id}`)}>Edit Price List</button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-7">
          <div className="card p-3">
            <h6 className="mb-3">Offer Details</h6>
            <div className="d-flex justify-content-between">
              <div>
                <div className="text-muted small" style={{ fontSize: '13px' }}>Category Name</div>
                <div className="fw-semibold">{categoryName || 'N/A'}</div>
              </div>
              <div>
                <div className="text-muted small" style={{ fontSize: '13px' }}>Start Date</div>
                <div className="fw-semibold">{startDateDisplay || 'N/A'}</div>
              </div>
              <div>
                <div className="text-muted small" style={{ fontSize: '13px' }}>End Date</div>
                <div className="fw-semibold">{endDateDisplay || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="card text-center p-3">
            <div className="text-muted small pt-3 fs-6" >Total Item</div>
            <div className="fw-bold" style={{ fontSize: 20 }}>{totalItems}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center p-3">
            <div className="text-muted small pt-3 fs-6">Offer Period</div>
            <div className="fw-bold fs-5">{offerPeriodDays ? `${offerPeriodDays} Days` : 'N/A'}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="fw-semibold mb-0" style={{ fontSize: "1.35rem" }}>
            Variant Information
            <span className="badge bg-primary ms-2">{variantRows.length}</span>
          </h3>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 280 }}
            placeholder="Search variants..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {variantRows.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <i className="fas fa-cube fa-3x text-muted"></i>
            </div>
            <h5 className="text-muted">No variants available</h5>
            <p className="text-muted">This item doesn't have any variants yet.</p>
          </div>
        ) : (
          <CommonTable
            columns={[
              { key: 'srNo', label: 'Sr. No' },
              { key: 'name', label: 'Item' },
              { key: 'originalPrice', label: 'Original Price(₹)' },
              { key: 'offerPrice', label: 'Offer Price(₹)' },
            ]}
            data={filteredRows.map(r => ({ srNo: r.srNo, name: r.name, originalPrice: r.originalPrice, offerPrice: r.offerPrice }))}
            itemsPerPageOptions={[10, 15, 25]}
            defaultItemsPerPage={15}
            bordered
          />
        )}
      </div>
    </div>
  );
}